import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/base/VideoCard';
import SectionHeader from '@/components/base/SectionHeader';
import PlayerDemo from '@/pages/detail/components/PlayerDemo';
import EpisodePanel from '@/pages/detail/components/EpisodePanel';
import { categoryPath, getEpisodes, getRelated, getVideoById, typeLabel } from '@/data/videos';
import { useSeo } from '@/hooks/useSeo';
import type { VideoType } from '@/types/video';

interface DetailPageProps {
  type: VideoType;
}

export default function DetailPage({ type }: DetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const label = typeLabel(type);
  const video = getVideoById(type, id ?? '');
  const episodes = useMemo(() => (video ? getEpisodes(video) : []), [video]);
  const [activeEpisodeId, setActiveEpisodeId] = useState('');

  useEffect(() => {
    setActiveEpisodeId(episodes.length > 0 ? episodes[0].id : '');
  }, [type, id, episodes]);

  const related = useMemo(() => (video ? getRelated(video, 6) : []), [video]);

  useSeo({
    title: video ? `${video.title} - 麻豆视频影视详情` : '影片不存在或已下架 - 麻豆视频',
    description: video
      ? `观看${label}《${video.title}》高清视频在线播放，包含导演、主演、剧情介绍及相关推荐链接。`
      : '该影片可能已下架或链接有误，返回首页浏览更多影视内容。',
    keywords: video
      ? `${video.title},${video.genres.join(',')},麻豆视频,在线播放,影视详情`
      : '麻豆视频,影视详情',
  });

  if (!video) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-20 text-center md:px-8 md:py-28">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            影片不存在或已下架
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-500">
            你访问的影片可能已从演示库中移除，或链接中的编号有误。可以返回首页或从分类页重新查找。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              返回首页
            </Link>
            <Link
              to={categoryPath(type)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-primary-600"
            >
              浏览{label}
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const activeEpisode = episodes.find((item) => item.id === activeEpisodeId) ?? null;
  const playerKey = `${video.id}-${activeEpisodeId || 'main'}`;

  const scrollToPlayer = () => {
    const anchor = document.getElementById('player-area');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <div className="animate-fade-in mx-auto w-full max-w-[1280px] px-4 pt-5 md:px-8 md:pt-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <Link to={categoryPath(type)} className="cursor-pointer transition-colors hover:text-primary-600">
            {label}
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="truncate text-foreground-800">{video.title}</span>
        </nav>

        <section className="relative mt-3 overflow-hidden rounded-lg bg-primary-950">
          <img
            src={video.backdrop ?? video.cover}
            alt={`${video.title}主视觉`}
            title={`${video.title} - ${label}详情`}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/40" />

          <div className="relative grid grid-cols-1 gap-6 p-5 md:grid-cols-[190px_1fr] md:gap-7 md:p-8">
            <div className="relative hidden aspect-[3/4] w-full overflow-hidden rounded-md bg-primary-900 md:block">
              <img
                src={video.cover}
                alt={`${video.title}封面`}
                title={`${video.title} - 高清影视在线观看`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex flex-col justify-center text-background-50">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold text-primary-950">
                  {label}
                </span>
                <span className="flex items-center gap-1 rounded-md bg-primary-900/70 px-2 py-1 font-label text-[11px] font-semibold text-accent-300">
                  <i className="ri-star-fill text-[10px]" />
                  {video.rating.toFixed(1)} 演示评分
                </span>
                {video.type === 'tv' && video.episodeCount && (
                  <span className="rounded-md bg-secondary-900/80 px-2 py-1 text-[11px] text-background-100">
                    全 {video.episodeCount} 集
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-4xl">
                {video.title}
              </h1>

              <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200 md:text-sm">
                <span>{video.year}</span>
                <span className="h-0.5 w-0.5 rounded-full bg-background-300/70" />
                <span>{video.regions.join(' / ')}</span>
                <span className="h-0.5 w-0.5 rounded-full bg-background-300/70" />
                <span>{video.duration}</span>
                <span className="h-0.5 w-0.5 rounded-full bg-background-300/70" />
                <span>导演 {video.director}</span>
              </p>

              <p className="mt-3 text-xs text-background-200 md:text-sm">
                <span className="text-primary-300">主演：</span>
                {video.cast.join(' / ')}
              </p>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {video.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-full border border-background-50/25 px-2.5 py-0.5 text-[11px] text-background-100"
                  >
                    {genre}
                  </span>
                ))}
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-50/10 px-2.5 py-0.5 text-[11px] text-background-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={scrollToPlayer}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-fill text-lg" />
                  在线播放演示
                </button>
                <Link
                  to={categoryPath(type)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-apps-2-line text-base" />
                  更多{label}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div>
            <div id="player-area" className="scroll-mt-20">
              <PlayerDemo video={video} episode={activeEpisode} playerKey={playerKey} />
            </div>

            {video.type === 'tv' && episodes.length > 0 && (
              <EpisodePanel
                episodes={episodes}
                activeId={activeEpisodeId}
                onSelect={(episode) => setActiveEpisodeId(episode.id)}
              />
            )}

            <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">{video.synopsis}</p>
              <p className="mt-4 rounded-md bg-background-100 px-3.5 py-3 text-[11px] leading-relaxed text-foreground-500">
                本页剧情、演职人员与评分为虚构演示内容，仅用于展示影视门户的信息结构；播放区域为界面演示，不含真实视频资源。
              </p>

              <h2 className="mt-6 flex items-center gap-1.5 font-heading text-base font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                影片信息
              </h2>
              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-800">{video.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-800">{video.cast.slice(0, 3).join(' / ')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">类型</dt>
                  <dd className="text-foreground-800">{video.genres.join(' / ')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">上映年份</dt>
                  <dd className="text-foreground-800">{video.year}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">时长</dt>
                  <dd className="text-foreground-800">{video.duration}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
                  <dd className="text-foreground-800">{video.regions.join(' / ')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">更新时间</dt>
                  <dd className="text-foreground-800">{video.updatedAt}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-foreground-500">所属分类</dt>
                  <dd>
                    <Link
                      to={categoryPath(type)}
                      className="cursor-pointer text-primary-600 underline-offset-2 hover:text-accent-600 hover:underline"
                    >
                      {label}分类
                    </Link>
                  </dd>
                </div>
              </dl>
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">播放说明</h2>
              <ul className="mt-3 space-y-2.5 text-xs leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-play-circle-line mt-0.5 text-base text-accent-600" />
                  <span>点击播放按钮后进入演示播放状态，进度条会自动推进，用于展示播放器界面效果。</span>
                </li>
                <li className="flex gap-2">
                  <i className="ri-play-list-2-line mt-0.5 text-base text-accent-600" />
                  <span>
                    {video.type === 'tv'
                      ? '切换剧集后播放区域标题与进度会同步重置，便于对比不同集数的展示效果。'
                      : '电影详情页不包含剧集选择，可直接体验播放与暂停交互。'}
                  </span>
                </li>
                <li className="flex gap-2">
                  <i className="ri-error-warning-line mt-0.5 text-base text-accent-600" />
                  <span>本站不提供真实视频源，播放结束后会提示演示已完成，不产生任何网络媒体请求。</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-primary-950 p-5 text-background-50">
              <h2 className="font-heading text-sm font-bold">数据与内容说明</h2>
              <p className="mt-3 text-xs leading-relaxed text-primary-200">
                影片条目、评分、导演与主演均为虚构示例，与该名称可能存在的真实作品无关。如需替换为自有内容，可在片库演示数据中统一调整。
              </p>
              <Link
                to="/search"
                className="mt-4 flex cursor-pointer items-center justify-between rounded-md border border-primary-800 px-3 py-2 text-xs text-primary-100 transition-colors hover:border-accent-500 hover:text-accent-300"
              >
                搜索更多影视内容
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-10 md:mt-12">
          <SectionHeader
            eyebrow="You may also like"
            title="相关推荐"
            subtitle={`根据类型与标签匹配，为《${video.title}》挑选的${label}内容。`}
            actionLabel={`更多${label}`}
            actionTo={categoryPath(type)}
          />
          {related.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-background-200 bg-background-50 px-4 py-10 text-center text-sm text-foreground-500">
              暂无推荐内容
            </p>
          )}
        </section>
      </div>
    </SiteLayout>
  );
}